import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Edit2, X } from "lucide-react";

interface AnalysisField {
  name: string;
  description: string;
  type: "string" | "enum" | "number" | "boolean";
  required?: boolean;
  choices?: string[];
}

interface PostCallAnalysisConfig {
  enabled: boolean;
  fields: AnalysisField[];
}

interface PostCallAnalysisEditorProps {
  config: PostCallAnalysisConfig;
  onChange: (config: PostCallAnalysisConfig) => void;
}

export function PostCallAnalysisEditor({ config, onChange }: PostCallAnalysisEditorProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<AnalysisField | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Form state
  const [fieldName, setFieldName] = useState("");
  const [fieldDescription, setFieldDescription] = useState("");
  const [fieldType, setFieldType] = useState<"string" | "enum" | "number" | "boolean">("string");
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldChoices, setFieldChoices] = useState<string[]>([]);
  const [newChoice, setNewChoice] = useState("");

  const resetForm = () => {
    setFieldName("");
    setFieldDescription("");
    setFieldType("string");
    setFieldRequired(false);
    setFieldChoices([]);
    setNewChoice("");
    setEditingField(null);
    setEditingIndex(null);
  };

  const openEditDialog = (field: AnalysisField, index: number) => {
    setFieldName(field.name);
    setFieldDescription(field.description);
    setFieldType(field.type);
    setFieldRequired(field.required || false);
    setFieldChoices(field.choices || []);
    setNewChoice("");
    setEditingField(field);
    setEditingIndex(index);
    setIsDialogOpen(true);
  };

  const MAX_FIELDS = 30;
  const MAX_ENUM_OPTIONS = 30;

  const handleSaveField = () => {
    const newField: AnalysisField = {
      name: fieldName.trim(),
      description: fieldDescription.trim(),
      type: fieldType,
      required: fieldRequired,
    };

    if (fieldType === "enum" && fieldChoices.length > 0) {
      newField.choices = fieldChoices.slice(0, MAX_ENUM_OPTIONS);
    }

    let updatedFields = [...config.fields];
    if (editingIndex !== null) {
      updatedFields[editingIndex] = newField;
    } else {
      if (updatedFields.length >= MAX_FIELDS) {
        return; // Prevent adding more than MAX_FIELDS
      }
      updatedFields.push(newField);
    }

    onChange({ ...config, fields: updatedFields });
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDeleteField = (index: number) => {
    const updatedFields = config.fields.filter((_, i) => i !== index);
    onChange({ ...config, fields: updatedFields });
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      string: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
      number: "bg-green-500/10 text-green-700 dark:text-green-400",
      boolean: "bg-purple-500/10 text-purple-700 dark:text-purple-400",
      enum: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
    };
    return colors[type] || "bg-gray-500/10";
  };

  return (
    <div className="space-y-4 pt-2">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Post Call Analysis</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Extract structured data from call transcripts using AI
          </p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(enabled) => onChange({ ...config, enabled })}
        />
      </div>

      {config.enabled && (
        <>
          {/* Fields List */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Analysis Fields</Label>
            {config.fields.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center border-2 border-dashed rounded-md">
                No fields configured. Add a field to start extracting data.
              </p>
            ) : (
              <div className="space-y-2">
                {config.fields.map((field, index) => (
                  <Card key={index} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm truncate">{field.name}</span>
                          <Badge variant="outline" className={getTypeColor(field.type)}>
                            {field.type}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {field.description}
                        </p>
                        {field.type === "enum" && field.choices && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Options: {field.choices.join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(field, index)}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteField(index)}
                        >
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Add Field Button */}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                disabled={config.fields.length >= MAX_FIELDS}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Field {config.fields.length >= MAX_FIELDS && `(${MAX_FIELDS} max)`}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingField ? "Edit Field" : "Add Analysis Field"}</DialogTitle>
                <DialogDescription>
                  Configure what data to extract from call transcripts
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Field Name</Label>
                  <Input
                    placeholder="e.g., summary, customer_type"
                    value={fieldName}
                    onChange={(e) => setFieldName(e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    placeholder="e.g., Summary of the call conversation"
                    value={fieldDescription}
                    onChange={(e) => setFieldDescription(e.target.value)}
                    className="mt-2"
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Field Type</Label>
                  <Select value={fieldType} onValueChange={(v: any) => setFieldType(v)}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="string">String (Text)</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="boolean">Boolean (Yes/No)</SelectItem>
                      <SelectItem value="enum">Enum (Multiple Choice)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {fieldType === "enum" && (
                  <div className="space-y-3">
                    <Label>Options</Label>
                    <div className="flex flex-wrap gap-2">
                      {fieldChoices.map((choice, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="pl-3 pr-1 py-1.5 flex items-center gap-1"
                        >
                          {choice}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 hover:bg-destructive/20"
                            onClick={() => {
                              setFieldChoices(fieldChoices.filter((_, i) => i !== index));
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add an option..."
                        value={newChoice}
                        onChange={(e) => setNewChoice(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newChoice.trim()) {
                            e.preventDefault();
                            if (!fieldChoices.includes(newChoice.trim()) && fieldChoices.length < MAX_ENUM_OPTIONS) {
                              setFieldChoices([...fieldChoices, newChoice.trim()]);
                            }
                            setNewChoice("");
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (newChoice.trim() && !fieldChoices.includes(newChoice.trim()) && fieldChoices.length < MAX_ENUM_OPTIONS) {
                            setFieldChoices([...fieldChoices, newChoice.trim()]);
                            setNewChoice("");
                          }
                        }}
                        disabled={!newChoice.trim() || fieldChoices.length >= MAX_ENUM_OPTIONS}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {fieldChoices.length === 0 
                        ? "Press Enter or click + to add options" 
                        : `${fieldChoices.length}/${MAX_ENUM_OPTIONS} options`}
                    </p>
                  </div>
                )}

              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveField}
                  disabled={!fieldName.trim() || !fieldDescription.trim()}
                >
                  {editingField ? "Update" : "Add"} Field
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
